import { extends as _extends, objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import IconLayout from './icon.esm.js';
import IconAfterTextLayout from './icon-after-text-layout.esm.js';
import IconBeforeTextLayout from './icon-before-text-layout.esm.js';
import IconBothEndsLayout from './icon-both-ends-layout.esm.js';
import IconTopOfTextLayout from './icon-top-of-text-layout.esm.js';
import { THEME_UI_DEFAULT_KEY } from '../../theme-provider/types.esm.js';
import { useSafeThemeUI } from '../../theme-provider/use-theme-ui.esm.js';
import extractStringNodes from '../../utils/extract-string-nodes.esm.js';
import { get, pick, omit, EMPTY_OBJECT } from '../../utils/object.esm.js';
import { getLayout, getPaddingY, getPaddingX, containsOmitPropSymbol, propValueIsSet, spacePropNames, omitPaddingX, omitPaddingY, omitLayout } from '../../utils/props-utils.esm.js';
import sizeSynchronize from '../../utils/size-synchronize.esm.js';
import { resolveSize, toBaseUnit } from '../../utils/size-utils.esm.js';
import Box from '../_primitives/box/box.esm.js';
import LoadingSpinner from '../_primitives/progress/loading-spinner/loading-spinner.esm.js';
import Text from '../_primitives/text/text.esm.js';
import { createUseStyledComponent } from '../../hooks/use-styled-component.esm.js';
import useTrueAtMediaBreakpointLessThen from '../../hooks/use-true-at-media-breakpoint-less-then.esm.js';

var _excluded = ["as", "role", "className", "disabled", "size", "icon", "iconBefore", "iconAfter", "iconTop", "children", "variant", "removeTextAt", "lhCrop", "textWrap", "alignItems", "justifyContent", "iconSize", "useIconsSize", "toggled", "isLoading", "textProps", "__themeKey", "__css", "sx", "automation-id", "hidden"],
  _excluded2 = ["sx"];
var IconTopOfTextLayoutMemo = /*#__PURE__*/React.memo(IconTopOfTextLayout);
var IconBeforeTextLayoutMemo = /*#__PURE__*/React.memo(IconBeforeTextLayout);
var IconAfterTextLayoutMemo = /*#__PURE__*/React.memo(IconAfterTextLayout);
var IconBothEndsLayoutMemo = /*#__PURE__*/React.memo(IconBothEndsLayout);
var IconLayoutMemo = /*#__PURE__*/React.memo(IconLayout);
var useStyledComponent = createUseStyledComponent(['button', 'a', 'li'], 'csg-button');
var ButtonPrivate = /*#__PURE__*/React.forwardRef(function _ButtonPrivate(props, ref) {
  var theme = useSafeThemeUI(props);
  var _theme$baseUnit = theme.baseUnit,
    baseUnit = _theme$baseUnit === void 0 ? 'px' : _theme$baseUnit;
  var _props$as = props.as,
    component = _props$as === void 0 ? 'button' : _props$as,
    _props$role = props.role,
    role = _props$role === void 0 ? 'button' : _props$role,
    className = props.className,
    disabled = props.disabled,
    size = props.size,
    iconProps = props.icon,
    iconBefore = props.iconBefore,
    iconAfter = props.iconAfter,
    iconTop = props.iconTop,
    children = props.children,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? THEME_UI_DEFAULT_KEY : _props$variant,
    removeTextAt = props.removeTextAt,
    lhCrop = props.lhCrop,
    textWrap = props.textWrap,
    _props$alignItems = props.alignItems,
    alignItems = _props$alignItems === void 0 ? 'center' : _props$alignItems,
    _props$justifyContent = props.justifyContent,
    justifyContent = _props$justifyContent === void 0 ? 'center' : _props$justifyContent,
    iconSizeProps = props.iconSize,
    useIconsSize = props.useIconsSize,
    toggled = props.toggled,
    isLoading = props.isLoading,
    _props$textProps = props.textProps,
    textProps = _props$textProps === void 0 ? EMPTY_OBJECT : _props$textProps,
    _props$__themeKey = props.__themeKey,
    __themeKey = _props$__themeKey === void 0 ? 'buttons' : _props$__themeKey,
    _props$__css = props.__css,
    __css = _props$__css === void 0 ? EMPTY_OBJECT : _props$__css,
    _props$sx = props.sx,
    sx = _props$sx === void 0 ? EMPTY_OBJECT : _props$sx,
    automationId = props['automation-id'],
    hidden = props.hidden,
    rest = _objectWithoutProperties(props, _excluded);
  var Component = useStyledComponent(component);
  // reconcile deprecated iconTop props
  var icon = iconProps !== null && iconProps !== void 0 ? iconProps : iconTop;
  var hasIcon = icon || iconBefore || iconAfter;
  var sxTextProps = textProps.sx,
    restTextProps = _objectWithoutProperties(textProps, _excluded2);
  var removeText = removeTextAt ?
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useTrueAtMediaBreakpointLessThen(removeTextAt) : false;
  var stringNodes = extractStringNodes(React.Children.toArray(children).filter(function (i) {
    return typeof i === 'string';
  }));
  var renderNodes = React.Children.toArray(children).filter(function (i) {
    return typeof i !== 'string' && typeof i !== 'undefined' && typeof i !== 'number' && typeof i !== 'boolean';
  });
  var buttonLabel = "".concat(stringNodes);
  var _useMemo = useMemo(function () {
      var isIconOnly = icon && (!buttonLabel || removeText) || renderNodes.length > 0;
      // this routine should be deprecated as we move away from using internal icon from theme-ui
      // to using icon from @costar/theme-ui-icon library. it's no longer necessary and size of icon
      // should be scale using combination of scale css and em unit depending on the icon.
      var _sizeSynchronize = sizeSynchronize(size !== null && size !== void 0 ? size : 'sm',
        // default to standard 1rem if not provide for default calculation
        theme, isIconOnly ? 0 : icon ? +1 : -1),
        fontSize = _sizeSynchronize.fontSize,
        spinnerSize = _sizeSynchronize.spinnerSize,
        iconSize = _sizeSynchronize.iconSize;
      return _objectSpread2(_objectSpread2({}, size ? {
        fontSize: fontSize
      } : {}), {}, {
        spinnerSize: spinnerSize,
        iconSize: iconSize,
        isIconOnly: isIconOnly
      }, iconSizeProps ? {
        iconSize: resolveSize(iconSizeProps, get(theme, 'sizes.icon'))
      } : {});
    }, [buttonLabel, icon, iconSizeProps, removeText, renderNodes.length, size, theme]),
    fontSize = _useMemo.fontSize,
    spinnerSize = _useMemo.spinnerSize,
    isIconOnly = _useMemo.isIconOnly,
    iconSize = _useMemo.iconSize;
  var combineFontSize = _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, {
    fontSize: fontSize
  }), pick(rest, ['fontSize'])), pick(__css, ['fontSize'])), pick(sx, ['fontSize']));

  // remove size from layout fields
  var combineLayout = omit(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, useIconsSize || icon && buttonLabel || icon ? {} : {
    height: '2em'
  }), getLayout(props)), getLayout(__css)), getLayout(sx)), ['size']);
  var gapProps = _objectSpread2(_objectSpread2({}, {
    gap: icon && !isIconOnly ? 'xxs' : 'xs'
  }), pick(combineLayout, ['gap']));

  // setting of padding props supersede em padding settings
  var combinePadding = _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, getPaddingX(__css)), getPaddingY(__css)), getPaddingX(props)), getPaddingY(props)), getPaddingX(sx)), getPaddingY(sx));
  var paddingProps = containsOmitPropSymbol(combinePadding) ? {} : propValueIsSet(combinePadding, spacePropNames) ? combinePadding : _objectSpread2(_objectSpread2({
    emPaddingX: isIconOnly ? 'sm' : 'xs'
  }, isIconOnly ? {
    emPaddingY: '0.4375em'
  } : Object.keys(combineLayout).filter(function (k) {
    return k.toLowerCase().includes('height') && !!combineLayout[k];
  }).length > 0 ? {} : {
    emPaddingY: 'xs-2'
  }), combinePadding);
  var buttonSize = useIconsSize && icon && !buttonLabel ? _objectSpread2({
    height: iconSize,
    width: iconSize
  }, toBaseUnit(combineLayout, baseUnit)) : toBaseUnit(combineLayout, baseUnit);
  var buttonStyle = _objectSpread2(_objectSpread2({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: iconBefore || iconAfter ? 'space-between' : justifyContent,
    position: 'relative'
  }, hasIcon ? gapProps : {}), pick(combineLayout, ['display']));
  function wrapSvg(children) {
    var shouldWrap = children && children.type && children.type.name && children.type.name.startsWith('Svg');
    function wrap() {
      var squareSize = resolveSize(iconSize, get(theme, 'sizes.icon'));
      return /*#__PURE__*/React.createElement(Box, {
        height: props.height ? props.height : squareSize,
        width: props.width ? props.width : squareSize
      }, children);
    }
    return shouldWrap ? wrap() : children;
  }
  var textSx = useMemo(function () {
    return _objectSpread2({
      '&.is-hidden': {
        opacity: 0
      }
    }, sxTextProps);
  }, [sxTextProps]);
  var child = icon && !isIconOnly ? /*#__PURE__*/React.createElement(IconTopOfTextLayoutMemo, {
    role: component === 'button' ? role : undefined,
    buttonLabel: buttonLabel,
    removeText: removeText,
    alignItems: alignItems,
    icon: icon,
    lhCrop: lhCrop,
    textWrap: textWrap,
    iconSize: iconSize,
    stringNodes: stringNodes,
    children: children,
    textProps: textProps,
    isLoading: isLoading
  }) : icon && isIconOnly ? /*#__PURE__*/React.createElement(IconLayoutMemo, {
    icon: icon,
    iconSize: iconSize,
    isLoading: isLoading
  }) : iconBefore && !iconAfter ? /*#__PURE__*/React.createElement(IconBeforeTextLayoutMemo, {
    role: component === 'button' ? role : undefined,
    buttonLabel: buttonLabel,
    alignItems: alignItems,
    iconBefore: iconBefore,
    lhCrop: lhCrop,
    textWrap: textWrap,
    iconSize: iconSize,
    stringNodes: stringNodes,
    children: children,
    textProps: textProps,
    isLoading: isLoading
  }) : iconAfter && !iconBefore ? /*#__PURE__*/React.createElement(IconAfterTextLayoutMemo, {
    role: component === 'button' ? role : undefined,
    buttonLabel: buttonLabel,
    alignItems: alignItems,
    iconAfter: iconAfter,
    lhCrop: lhCrop,
    textWrap: textWrap,
    iconSize: iconSize,
    stringNodes: stringNodes,
    children: children,
    textProps: textProps,
    isLoading: isLoading
  }) : iconAfter && iconBefore ? /*#__PURE__*/React.createElement(IconBothEndsLayoutMemo, {
    role: component === 'button' ? role : undefined,
    buttonLabel: buttonLabel,
    alignItems: alignItems,
    iconBefore: iconBefore,
    iconAfter: iconAfter,
    lhCrop: lhCrop,
    textWrap: textWrap,
    iconSize: iconSize,
    stringNodes: stringNodes,
    children: children,
    textProps: textProps,
    isLoading: isLoading
  }) : stringNodes.length > 0 ? /*#__PURE__*/React.createElement(Text, _extends({
    className: classNames({
      'is-hidden': isLoading
    }),
    lhCrop: lhCrop,
    ellipsis: !textWrap,
    content: "".concat(stringNodes),
    role: component === 'button' ? role : undefined,
    sx: textSx
  }, restTextProps), stringNodes) : wrapSvg(children);
  return /*#__PURE__*/React.createElement(Component, _extends({
    ref: ref,
    __themeKey: __themeKey,
    variant: variant,
    a11yLabel: buttonLabel,
    className: classNames({
      'csg-tui-button': true,
      'is-toggled': toggled,
      'is-hidden': hidden
    }, className)
  }, _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({
    resetCss: {
      appearance: 'none',
      textDecoration: 'none',
      hyphens: 'auto',
      outline: 'unset'
    },
    'automation-id': automationId
  }, component === 'button' ? {
    type: 'button',
    disabled: disabled
  } : {
    role: role
  }), component !== 'button' ? {
    'aria-disabled': disabled
  } : {}), combineFontSize), paddingProps), icon && !isIconOnly ? {
    flexDirection: 'column'
  } : {}), {}, {
    __css: _objectSpread2(_objectSpread2({}, buttonStyle), omitPaddingX(omitPaddingY(omitLayout(omit(__css, ['fontSize', 'gap']))))),
    sx: omit(sx, ['fontSize', 'gap'])
  }, omitPaddingX(omitPaddingY(omitLayout(omit(rest, ['fontSize', 'gap']))))), omit(buttonSize, ['display']))), isLoading ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(LoadingSpinner, {
    size: spinnerSize,
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)"
  }), child) : child);
});
var MemoButton = /*#__PURE__*/React.memo(ButtonPrivate);
var Button = /*#__PURE__*/React.forwardRef(function Button(props, ref) {
  return /*#__PURE__*/React.createElement(MemoButton, _extends({
    ref: ref
  }, props));
});

export default Button;
export { ButtonPrivate };
//# sourceMappingURL=button.esm.js.map
